import React from 'react';
import { CountryCard } from '../components/CountryCard';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { Country, TodoList, Task } from '../types/todo';
import { getUserLists, getCountriesFromFirebase } from '../lib/firebase-service';
import { useAuth } from '../lib/auth-context';
import { InterviewSlider } from '../components/InterviewSlider';
import { OnboardingModal } from '../components/OnboardingModal';
import { QuickLinksSection } from '../components/QuickLinksSection';
import * as amplitude from '@amplitude/analytics-browser';

interface HomePageProps {
  userLists: string[];
  onCreateList: () => void;
  onManageList: (code: string) => void;
  onDeleteList: (code: string, fromDashboard: boolean) => void;
  availableCountries: Country[];
  isSelectingCountry: boolean;
  onCloseSelection: () => void;
  onSelectCountry: (code: string) => void;
  countries: Country[];
  isAuthenticated: boolean;
}

export function HomePage({
  userLists,
  onCreateList,
  onManageList,
  onDeleteList,
  availableCountries,
  isSelectingCountry,
  onCloseSelection,
  onSelectCountry,
  countries,
  isAuthenticated
}: HomePageProps) {
  const { user, userInfo } = useAuth();
  const [userListsData, setUserListsData] = React.useState<TodoList[]>([]);
  const [isVisible, setIsVisible] = React.useState(false);
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [touchStart, setTouchStart] = React.useState<number | null>(null);
  const [touchEnd, setTouchEnd] = React.useState<number | null>(null);
  const [firebaseCountries, setFirebaseCountries] = React.useState<Country[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const hasTrackedRef = React.useRef(false);
  const [showOnboarding, setShowOnboarding] = React.useState(false);

  React.useEffect(() => {
    setIsVisible(true);

    const fetchCountries = async () => {
      try {
        const countries = await getCountriesFromFirebase();
        const sortedCountries = countries.sort((a, b) =>
          a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' })
        );
        setFirebaseCountries(sortedCountries);
      } catch (error) {
        console.error('Error fetching countries:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCountries();

    const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding') === 'true';
    setShowOnboarding(!hasSeenOnboarding);
  }, []);

  React.useEffect(() => {
    if (!hasTrackedRef.current) {
      amplitude.track("Home View");
      hasTrackedRef.current = true;
    }
  }, []);

  React.useEffect(() => {
    if (user) {
      getUserLists(user.uid).then((lists) => {
        setUserListsData(lists);
      });
    }
  }, [user]);

  const userCountries = React.useMemo(() => {
    return firebaseCountries
      .filter(country => userLists.includes(country.code))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }));
  }, [firebaseCountries, userLists]);

  const otherCountries = React.useMemo(() => {
    return firebaseCountries
      .filter(country => !userLists.includes(country.code))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }));
  }, [firebaseCountries, userLists]);

  const countryGroups = otherCountries.reduce((acc, country, index) => {
    const groupIndex = Math.floor(index / 4);
    if (!acc[groupIndex]) {
      acc[groupIndex] = [];
    }
    acc[groupIndex].push(country);
    return acc;
  }, [] as Country[][]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % countryGroups.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + countryGroups.length) % countryGroups.length);
  };

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] pt-14">
        <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <>
      {showOnboarding && <OnboardingModal onClose={() => setShowOnboarding(false)} />}

      <div className={`transition-opacity duration-500 main-content ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="max-w-[1400px] mx-auto px-2 sm:px-6 lg:px-8 space-y-6 sm:space-y-14 pt-20 pb-8 sm:pt-28 sm:pb-12">
          <QuickLinksSection />

          {userCountries.length > 0 && (
            <section>
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-5">
                Mes checklists
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
                {userCountries.map((country) => {
                  const userList = userListsData.find(list => list.countryCode === country.code);
                  return (
                    <CountryCard
                      key={country.code}
                      country={country}
                      onCreateList={() => onSelectCountry(country.code)}
                      onManageList={onManageList}
                      onDeleteList={onDeleteList}
                      tasks={userList?.tasks || []}
                      departureDate={userList?.departureDate}
                      returnDate={userList?.returnDate}
                      createdAt={userList?.createdAt}
                    />
                  );
                })}
              </div>
            </section>
          )}

          <section>
            <div className="text-center mb-5 sm:mb-10">
              <h2 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                {userCountries.length > 0 ? 'Autres destinations' : 'Destinations PVT'}
              </h2>
              <p className="text-xs sm:text-base text-gray-500 dark:text-gray-400 max-w-lg mx-auto">
                {otherCountries.length} pays disponibles
              </p>
            </div>

            {/* Mobile Carousel */}
            <div className="md:hidden relative">
              <div
                className="overflow-hidden touch-pan-y rounded-xl"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <div
                  className="flex transition-transform duration-500 ease-out"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {countryGroups.map((group, groupIndex) => (
                    <div
                      key={groupIndex}
                      className="w-full flex-shrink-0 grid grid-cols-2 gap-1.5"
                    >
                      {group.map((country) => (
                        <button
                          key={country.code}
                          onClick={() => onSelectCountry(country.code)}
                          className="group relative overflow-hidden rounded-xl aspect-[3/4]"
                        >
                          <img
                            src={country.image}
                            alt={country.name}
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                          <div className="absolute inset-0 p-2.5 flex flex-col justify-between">
                            <div className="flex justify-end">
                              <span className="text-2xl">{country.flag}</span>
                            </div>
                            <div>
                              <h3 className="text-[15px] font-bold text-white mb-1.5">{country.name}</h3>
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/15 backdrop-blur-sm rounded-md text-white text-[11px] font-medium">
                                Explorer
                                <ArrowRight className="w-2.5 h-2.5" />
                              </span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {countryGroups.length > 1 && (
                <div className="flex justify-center gap-1.5 mt-4">
                  {countryGroups.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        currentSlide === index
                          ? 'w-6 bg-gray-900 dark:bg-white'
                          : 'w-1.5 bg-gray-300 dark:bg-gray-600'
                      }`}
                      aria-label={`Page ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Desktop Grid */}
            <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {otherCountries.map((country) => (
                <button
                  key={country.code}
                  onClick={() => onSelectCountry(country.code)}
                  className="group relative overflow-hidden rounded-2xl aspect-[3/4] hover:shadow-xl transition-all duration-300"
                >
                  <img
                    src={country.image}
                    alt={country.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <div className="absolute inset-0 p-5 flex flex-col justify-between">
                    <div className="flex justify-end">
                      <span className="text-4xl">{country.flag}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-3">{country.name}</h3>
                      <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-lg text-white text-sm font-medium group-hover:bg-white/25 transition-colors">
                        Explorer
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>

          <section>
            <InterviewSlider />
          </section>
        </div>
      </div>
    </>
  );
}
